import { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import logo from '../assets/images/logo-mark.png';
import FormInput from '../components/FormInput';
import Icon from '../components/Icon';
import { looksBlocked, SERVER_BLOCKED_MESSAGE } from '../global/func';

const Login = ({ LoginSuccess, onForgotPassword, onCreateAccount }) => {

    // In state, not in module-level `let`s.
    //
    // The old version kept email and password in variables beside the component
    // and assigned the User prop on success. Neither survives a re-render and
    // neither belongs to this component, so the form could not show what it
    // held, could not be validated, and could not disable itself while a
    // request was in flight.
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const signIn = async (e) => {
        e.preventDefault();
        if (busy) return;

        if (!email.trim() || !password) {
            setError('Enter your email address and password.');
            return;
        }

        setBusy(true);
        setError('');

        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/auth/');
        const basicAuth = 'Basic ' + btoa(email.trim() + ':' + password);

        try {
            // TODO: the credentials still travel as a query parameter. Moving
            // them to a header needs the API to answer a CORS preflight, which
            // the older endpoints do not.
            const response = await axios.get(url, { params: { Authorization: basicAuth } });

            // A greylisted connection gets the host's challenge page with a 200,
            // so the status says success and response.data.data is not there.
            // Reading data[0] off that threw, landed in the catch, and told the
            // customer their password was wrong -- sending them to retype
            // something that was never the problem.
            if (looksBlocked(response.data)) {
                setError(SERVER_BLOCKED_MESSAGE);
                return;
            }

            const account = response.data?.data?.[0];
            if (response.status === 200 && account) {
                await LoginSuccess(account);
                return;
            }
            setError('That email address and password do not match an account.');
        } catch (err) {
            if (looksBlocked(err.response?.data)) {
                setError(SERVER_BLOCKED_MESSAGE);
            } else if (!err.response) {
                setError('Could not reach the server. Check your connection and try again.');
            } else {
                setError('That email address and password do not match an account.');
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="column auth" onSubmit={signIn}>
            <img className="auth__logo" src={logo} alt="Prayer Box" />
            <h1 className="auth__title">Prayer Box</h1>
            <p className="auth__sub">Sign in to set up your box.</p>

            {error && <div className="notice notice--warn" role="alert">{error}</div>}

            <FormInput
                name="email"
                labelText="Email address"
                inputType="email"
                value={email}
                maxLen={80}
                isValid={error ? false : undefined}
                onValueChange={setEmail} />

            <FormInput
                name="password"
                labelText="Password"
                inputType="password"
                value={password}
                maxLen={80}
                isValid={error ? false : undefined}
                onValueChange={setPassword} />

            <button type="submit" className="btn btn--primary btn--block" disabled={busy}
                    style={{ marginTop: '.5rem' }}>
                {busy ? <span className="spinner" /> : <Icon name="signout" size={16} />}
                {busy ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="auth__foot">
                <p style={{ margin: '.8rem 0 .3rem' }}>
                    <button type="button" onClick={onForgotPassword}>Forgot your password?</button>
                </p>
                <p style={{ margin: 0 }}>
                    Just got a Prayer Box?{' '}
                    <button type="button" onClick={onCreateAccount}>Create an account</button>
                </p>
            </div>
        </form>
    );
};

Login.propTypes = {
    LoginSuccess: PropTypes.func.isRequired,
    User: PropTypes.object,
    onForgotPassword: PropTypes.func.isRequired,
    onCreateAccount: PropTypes.func.isRequired,
};

export default Login;
